/**
 * Enhanced Error Logger for Development and Production
 * Provides detailed error tracking with source maps support
 */

class ErrorLogger {
  constructor() {
    this.errors = [];
    this.maxErrors = 50;
    this.isDevelopment = process.env.NODE_ENV !== 'production';
  }

  logError(error, context = {}) {
    const errorEntry = {
      timestamp: new Date().toISOString(),
      message: error.message || error.toString(),
      stack: error.stack,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    this.errors.push(errorEntry);
    
    // Keep only last N errors
    if (this.errors.length > this.maxErrors) {
      this.errors.shift();
    }

    // Enhanced console logging
    console.group(`🔴 Error Logged: ${errorEntry.message}`);
    console.error('Error Object:', error);
    console.table({
      'Timestamp': errorEntry.timestamp,
      'URL': errorEntry.url,
      'Context': JSON.stringify(context),
    });
    console.error('Stack Trace:', error.stack);
    console.groupEnd();

    // Store in localStorage for debugging
    try {
      localStorage.setItem('lastError', JSON.stringify(errorEntry));
      localStorage.setItem('errorHistory', JSON.stringify(this.errors.slice(-10)));
    } catch (e) {
      console.warn('Could not save error to localStorage:', e);
    }

    return errorEntry;
  }

  logWarning(message, context = {}) {
    console.group(`⚠️ Warning: ${message}`);
    console.warn('Context:', context);
    console.trace('Warning trace');
    console.groupEnd();
  }

  getErrors() {
    return this.errors;
  }

  clearErrors() {
    this.errors = [];
    try {
      localStorage.removeItem('lastError');
      localStorage.removeItem('errorHistory');
    } catch (e) {
      console.warn('Could not clear error storage:', e);
    }
  }

  getLastError() {
    try {
      const lastError = localStorage.getItem('lastError');
      return lastError ? JSON.parse(lastError) : null;
    } catch (e) {
      return null;
    }
  }
}

// Global error handler
const errorLogger = new ErrorLogger();

// Catch unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  errorLogger.logError(event.reason, {
    type: 'unhandledRejection',
    promise: event.promise,
  });
});

// Catch global errors
window.addEventListener('error', (event) => {
  errorLogger.logError(event.error || new Error(event.message), {
    type: 'globalError',
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

// Make available globally for debugging
window.errorLogger = errorLogger;

export default errorLogger;
